import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener, inject,
  Input, OnDestroy, OnInit,
  Output, signal,
  ViewChild, WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import {Pixel, PixelChunkResponse, ReloadMode} from '@/app/core/models/p-place/pixel';
import {Colour} from '@/app/core/models/p-place/colour';
import {debounceTime, distinctUntilChanged, Subject, Subscription} from 'rxjs';
import {ChunkService} from '@services/p-place/chunk.service';
import {WebSocketService} from '@services/p-place/websocket.service';
import {toLocalInputFormat} from '@/app/core/utils/dateUtils';

interface PaintedPixel {
	x: number;
	y: number;
	newColor: Colour;
	originalColor: string;
	placedAt: string;
	validated: boolean;
}

@Component({
	selector: 'peps-canvas',
	imports: [],
	templateUrl: './canvas.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './canvas.component.css'
})
export class CanvasComponent implements AfterViewInit, OnInit, OnDestroy {

	private chunkService = inject(ChunkService);
	private webSocketService = inject(WebSocketService);

	@ViewChild('canvas', {static: false}) canvasRef!: ElementRef<HTMLCanvasElement>;

	@Input() selectedColour: Colour | null = null;
	@Input() canPlacePixel: boolean = true;
	@Input() availablePixels: WritableSignal<number> = signal(0);

	@Output() pixelPlaced = new EventEmitter<number>();
	@Output() pixelRemoved = new EventEmitter<number>();
	@Output() pixelValidated = new EventEmitter<Pixel[]>();
	@Output() pixelSelected = new EventEmitter<Pixel>();

	private ctx!: CanvasRenderingContext2D;

	private offscreenCanvas!: HTMLCanvasElement;
	private offscreenCtx!: CanvasRenderingContext2D;

	private initialOffscreenCanvas!: HTMLCanvasElement;

	private readonly CANVAS_WIDTH = 10000;
	private readonly CANVAS_HEIGHT = 10000;

	private cameraX = 0;
	private cameraY = 0;
	private zoom = 5;

	private isDragging = false;
	private dragStartX = 0;
	private dragStartY = 0;
	private cameraStartX = 0;
	private cameraStartY = 0;

	private cameraMoveSubject$ = new Subject<{ x: number; y: number; zoom: number }>();
	private cameraMoveSub?: Subscription;

	private animationFrameId: number | null = null;
	public selectedPixel: { x: number, y: number } | null = null;

	private paintedPixelsMap: Map<string, PaintedPixel> = new Map();
	private pixelMetadataMap: Map<string, Pixel> = new Map();

	private readonly CHUNK_SIZE = 100;

	private loadedChunks: Set<string> = new Set();

	public reloadMode: ReloadMode = 'hybrid';
	public reloadInterval = 15000;

	private chunkSubscription?: Subscription;
	private chunksSubscription?: Subscription;
	private connectionStatusSubscription?: Subscription;

	public isWebSocketConnected = false;
	private lastTouchDistance: number | null = null;
	private lastTouchCenter: { x: number; y: number } | null = null;
	private touchStartTime = 0;
	private isTouchDragging = false;
	private longPressTimeout: any = null;
	private isLongPress = false;

	ngOnInit(): void {
		this.subscribeToChunks();
		this.subscribeToChunkUpdates();
		this.connectWebSocket();

		this.cameraMoveSub = this.cameraMoveSubject$
			.pipe(
				debounceTime(300),
				distinctUntilChanged((prev, curr) =>
					Math.abs(prev.x - curr.x) < 10 &&
					Math.abs(prev.y - curr.y) < 10 &&
					Math.abs(prev.zoom - curr.zoom) < 0.01
				)
			)
			.subscribe(() => {
				this.loadVisibleChunks();
			});
	}

	ngOnDestroy(): void {
		this.chunkSubscription?.unsubscribe();
		this.chunksSubscription?.unsubscribe();

		this.connectionStatusSubscription?.unsubscribe();
		this.cameraMoveSub?.unsubscribe();

		this.chunkService.stopAutoReload();

		this.webSocketService.disconnect();
	}

	ngAfterViewInit(): void {
		const canvas = this.canvasRef.nativeElement;
		this.ctx = canvas.getContext('2d', {alpha: false})!;

		this.offscreenCanvas = document.createElement('canvas');
		this.offscreenCanvas.width = this.CANVAS_WIDTH;
		this.offscreenCanvas.height = this.CANVAS_HEIGHT;
		this.offscreenCtx = this.offscreenCanvas.getContext('2d', {alpha: false})!;

		this.resizeCanvas();

		this.centerCamera();

		this.drawStaticContent();
		this.saveInitialCanvas();

	}

	private subscribeToChunkUpdates(): void {

		this.chunkService.onChunksUpdated().subscribe({
			next: (notification) => {
			}
		});
	}

	public validatePixels(): void {
		if (this.paintedPixelsMap.size === 0) {
			return;
		}

		if (!this.isWebSocketConnected) {
			alert('❌ WebSocket non connecté. Veuillez réessayer.');
			return;
		}

		const pixelsToSave: Pixel[] = Array.from(this.paintedPixelsMap.values()).map(p => ({
			x: p.x,
			y: p.y,
			colour: p.newColor,
			placedAt: p.placedAt
		}));

		this.pixelValidated.emit(pixelsToSave);
		this.paintedPixelsMap.clear();
		this.saveInitialCanvas();
	}

	/**
	 * Connecte au WebSocket
	 */
	private connectWebSocket(): void {
		this.webSocketService.connect();

		this.connectionStatusSubscription = this.webSocketService.getConnectionStatus().subscribe({
			next: (connected) => {
				this.isWebSocketConnected = connected;

				if (connected) {

					setTimeout(() => {
						this.loadVisibleChunks();

						this.chunkService.startAutoReload(this.reloadInterval, this.reloadMode);
					}, 100);
				} else {
					this.chunkService.stopAutoReload();
				}
			}
		});
	}

	/**
	 * S'abonne aux réponses de chunks
	 */
	private subscribeToChunks(): void {
		this.chunkSubscription = this.chunkService.onChunkReceived().subscribe({
			next: (chunk) => {
				this.handleChunkReceived(chunk);
			},
			error: (error) => {
				console.error('❌ Erreur réception chunk:', error);
			}
		});

		this.chunksSubscription = this.chunkService.onChunksReceived().subscribe({
			next: (chunks) => {
				chunks.forEach(chunk => this.handleChunkReceived(chunk));
			},
			error: (error) => {
				console.error('❌ Erreur réception chunks:', error);
			}
		});
	}

	private loadVisibleChunks(): void {
		if (!this.isWebSocketConnected) {
			return;
		}

		const canvas = this.canvasRef.nativeElement;

		const visibleChunks = this.chunkService.calculateVisibleChunks(
			this.cameraX,
			this.cameraY,
			canvas.width,
			canvas.height,
			this.zoom,
			this.CHUNK_SIZE
		);

		if (visibleChunks.length > 0) {
			this.chunkService.requestMultipleChunks(visibleChunks);
		}
	}

	/**
	 * Appelé quand la caméra bouge
	 */
	private onCameraMove(): void {
		this.cameraMoveSubject$.next({
			x: this.cameraX,
			y: this.cameraY,
			zoom: this.zoom
		});
	}

	/**
	 * Traite un chunk reçu
	 */
	private handleChunkReceived(chunk: PixelChunkResponse): void {
		const chunkKey = this.getChunkKey(chunk.chunkX, chunk.chunkY);

		this.loadedChunks.add(chunkKey);

		if (!chunk.pixels || chunk.pixels.length === 0) {
			return;
		}

		let pixelsDrawn = 0;
		chunk.pixels.forEach(pixel => {
			if (!pixel.colour) {
				return;
			}
			const pixelKey = this.getPixelKey(pixel.x, pixel.y);
			this.pixelMetadataMap.set(pixelKey, pixel);

			this.offscreenCtx.fillStyle = pixel.colour.hexCode;
			this.offscreenCtx.fillRect(pixel.x, pixel.y, 1, 1);

			const initialCtx = this.initialOffscreenCanvas.getContext('2d')!;
			initialCtx.fillStyle = pixel.colour.hexCode;
			initialCtx.fillRect(pixel.x, pixel.y, 1, 1);

			pixelsDrawn++;
		});

		this.requestRender();
	}

	/**
	 * Génère une clé unique pour un chunk
	 */
	private getChunkKey(chunkX: number, chunkY: number): string {
		return `${chunkX},${chunkY}`;
	}


	@HostListener('window:resize')
	resizeCanvas(): void {
		const canvas = this.canvasRef.nativeElement;
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
		this.requestRender();
	}

	/**
	 * Centre la caméra sur le canvas
	 */
	public centerCamera(): void {
		const canvas = this.canvasRef.nativeElement;
		this.cameraX = (this.CANVAS_WIDTH - canvas.width / this.zoom) / 2;
		this.cameraY = (this.CANVAS_HEIGHT - canvas.height / this.zoom) / 2;

		this.clampCamera();
		this.onCameraMove();
		this.requestRender();
	}

	/**
	 * Dessine tout le contenu statique sur le canvas off-screen UNE SEULE FOIS
	 */
	drawStaticContent(): void {
		this.offscreenCtx.fillStyle = '#FFFFFF';
		this.offscreenCtx.fillRect(0, 0, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);

		this.offscreenCtx.imageSmoothingEnabled = false;

	}

	private saveInitialCanvas(): void {
		this.initialOffscreenCanvas = document.createElement('canvas');
		this.initialOffscreenCanvas.width = this.CANVAS_WIDTH;
		this.initialOffscreenCanvas.height = this.CANVAS_HEIGHT;
		const initialCtx = this.initialOffscreenCanvas.getContext('2d', {alpha: false, willReadFrequently: true})!;
		initialCtx.drawImage(this.offscreenCanvas, 0, 0);
	}

	/**
	 * Demande un rendu (optimisé avec requestAnimationFrame)
	 */
	requestRender(): void {
		if (this.animationFrameId === null) {
			this.animationFrameId = requestAnimationFrame(() => {
				this.render();
				this.animationFrameId = null;
			});
		}
	}

	/**
	 * Rendu du canvas (O(1) grâce au canvas off-screen)
	 */
	render(): void {
		if (!this.ctx) return;

		const canvas = this.canvasRef.nativeElement;

		const sourceX = Math.max(0, this.cameraX);
		const sourceY = Math.max(0, this.cameraY);
		const sourceWidth = Math.min(canvas.width / this.zoom, this.CANVAS_WIDTH - sourceX);
		const sourceHeight = Math.min(canvas.height / this.zoom, this.CANVAS_HEIGHT - sourceY);

		const destX = Math.max(0, -this.cameraX * this.zoom);
		const destY = Math.max(0, -this.cameraY * this.zoom);
		const destWidth = sourceWidth * this.zoom;
		const destHeight = sourceHeight * this.zoom;

		this.ctx.fillStyle = '#FFFFFF';
		this.ctx.fillRect(0, 0, canvas.width, canvas.height);

		this.ctx.imageSmoothingEnabled = false;

		this.ctx.drawImage(
			this.offscreenCanvas,
			sourceX, sourceY, sourceWidth, sourceHeight,
			destX, destY, destWidth, destHeight
		);

		if (this.selectedPixel) {
			this.drawSelectedPixelBorder();
		}
	}

	/**
	 * Dessine une bordure autour du pixel sélectionné
	 */
	drawSelectedPixelBorder(): void {
		if (!this.selectedPixel) return;

		const pixelX = this.selectedPixel.x;
		const pixelY = this.selectedPixel.y;

		const screenX = (pixelX - this.cameraX) * this.zoom;
		const screenY = (pixelY - this.cameraY) * this.zoom;
		const screenSize = this.zoom;

		this.ctx.strokeStyle = '#020618';
		this.ctx.lineWidth = Math.max(4, this.zoom * 0.05);
		this.ctx.setLineDash([4, 2]);

		this.ctx.strokeRect(
			screenX + 1,
			screenY + 1,
			screenSize - 2,
			screenSize - 2
		);
		this.ctx.setLineDash([]);

		const px = this.pixelMetadataMap.get(this.getPixelKey(pixelX, pixelY));
		if(px) {
			this.pixelSelected.emit(this.pixelMetadataMap.get(this.getPixelKey(pixelX, pixelY)));
		} else {
			this.pixelSelected.emit({
				x: pixelX,
				y: pixelY
			});
		}
	}

	onWheel(event: WheelEvent): void {
		event.preventDefault();

		const canvas = this.canvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();
		const mouseX = event.clientX - rect.left;
		const mouseY = event.clientY - rect.top;

		const worldX = this.cameraX + mouseX / this.zoom;
		const worldY = this.cameraY + mouseY / this.zoom;

		const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
		const newZoom = this.zoom * zoomFactor;

		this.zoom = Math.max(0.19, Math.min(50, newZoom));

		this.cameraX = worldX - mouseX / this.zoom;
		this.cameraY = worldY - mouseY / this.zoom;

		this.clampCamera();
		this.onCameraMove();
		this.requestRender();
	}

	onMouseDown(event: MouseEvent): void {
		if (event.button === 0) {
			this.isDragging = true;
			this.dragStartX = event.clientX;
			this.dragStartY = event.clientY;
			this.cameraStartX = this.cameraX;
			this.cameraStartY = this.cameraY;
			event.preventDefault();
		}

		if (event.button === 2) {
			const worldPos = this.screenToWorld(event.clientX, event.clientY);
			const pixelX = Math.floor(worldPos.x);
			const pixelY = Math.floor(worldPos.y);

			if (pixelX >= 0 && pixelX < this.CANVAS_WIDTH &&
				pixelY >= 0 && pixelY < this.CANVAS_HEIGHT) {
				this.removePixel(pixelX, pixelY);
			}
			event.preventDefault();
		}
	}

	onMouseMove(event: MouseEvent): void {
		if (!this.isDragging) {
			return;
		}

		const deltaX = event.clientX - this.dragStartX;
		const deltaY = event.clientY - this.dragStartY;

		this.cameraX = this.cameraStartX - deltaX / this.zoom;
		this.cameraY = this.cameraStartY - deltaY / this.zoom;

		this.clampCamera();
		this.onCameraMove();

		this.requestRender();
	}

	onMouseUp(event: MouseEvent): void {
		if (event.button === 0 && this.isDragging) {
			const deltaX = Math.abs(event.clientX - this.dragStartX);
			const deltaY = Math.abs(event.clientY - this.dragStartY);

			if (deltaX < 5 && deltaY < 5) {
				const worldPos = this.screenToWorld(event.clientX, event.clientY);
				const pixelX = Math.floor(worldPos.x);
				const pixelY = Math.floor(worldPos.y);

				if (pixelX >= 0 && pixelX < this.CANVAS_WIDTH &&
					pixelY >= 0 && pixelY < this.CANVAS_HEIGHT) {
					if (this.selectedColour && this.canPlacePixel) {
						this.placePixel(pixelX, pixelY, this.selectedColour);
						this.selectedPixel = null;
					}

					this.selectedPixel = {x: pixelX, y: pixelY};

					this.requestRender();
				}
			}
		}

		this.isDragging = false;
	}

	private getPixelKey(x: number, y: number): string {
		return `${x},${y}`;
	}

	private removePixel(x: number, y: number): void {
		const key = this.getPixelKey(x, y);
		const paintedPixel = this.paintedPixelsMap.get(key);

		if (!paintedPixel) {
			return;
		}

		this.offscreenCtx.fillStyle = paintedPixel.originalColor;
		this.offscreenCtx.fillRect(x, y, 1, 1);

		this.paintedPixelsMap.delete(key);

		this.pixelRemoved.emit(1)

		this.requestRender();
	}

	private placePixel(x: number, y: number, color: Colour): void {
		if (this.availablePixels() <= 0) {
			return;
		}

		const key = this.getPixelKey(x, y);
		const existingPixel = this.paintedPixelsMap.get(key);

		if (existingPixel && !existingPixel.validated) {
			if (existingPixel.newColor.hexCode !== color.hexCode) {
				existingPixel.newColor = color;
				existingPixel.placedAt = toLocalInputFormat(new Date().toString());

				this.offscreenCtx.fillStyle = color.hexCode;
				this.offscreenCtx.fillRect(x, y, 1, 1);
				this.paintedPixelsMap.set(key, existingPixel);
				this.requestRender();
			}
			return;
		}

		if (existingPixel && existingPixel.validated && existingPixel.newColor.hexCode === color.hexCode) {
			return;
		}

		const originalColor = existingPixel?.originalColor || this.getOriginalColor(x, y);
		const now = toLocalInputFormat(new Date().toString());

		this.offscreenCtx.fillStyle = color.hexCode;
		this.offscreenCtx.fillRect(x, y, 1, 1);

		this.paintedPixelsMap.set(key, {
			x,
			y,
			newColor: color,
			originalColor,
			placedAt: now,
			validated: false
		});

		this.pixelPlaced.emit(1);
	}


	@HostListener('contextmenu', ['$event'])
	onContextMenu(event: Event): void {
		event.preventDefault();
	}

	private screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
		const canvas = this.canvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();

		const canvasX = screenX - rect.left;
		const canvasY = screenY - rect.top;

		const worldX = this.cameraX + canvasX / this.zoom;
		const worldY = this.cameraY + canvasY / this.zoom;

		return { x: worldX, y: worldY };
	}

	/**
	 * Limite la caméra aux bordures du canvas
	 */
	private clampCamera(): void {
		const canvas = this.canvasRef.nativeElement;
		const viewWidth = canvas.width / this.zoom;
		const viewHeight = canvas.height / this.zoom;

		this.cameraX = Math.max(0, Math.min(this.CANVAS_WIDTH - viewWidth, this.cameraX));
		this.cameraY = Math.max(0, Math.min(this.CANVAS_HEIGHT - viewHeight, this.cameraY));
	}

	download() {
		this.offscreenCanvas.toBlob((blob) => {
			if (blob) {
				const url = URL.createObjectURL(blob);

				const link = document.createElement('a');
				link.href = url;
				link.download = `pplace-${Date.now()}.png`;

				document.body.appendChild(link);
				link.click();

				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}
		}, 'image/png');
	}

	private getOriginalColor(x: number, y: number): string {
		const initialCtx = this.initialOffscreenCanvas.getContext('2d')!;
		const pixelData = initialCtx.getImageData(x, y, 1, 1).data;

		const r = pixelData[0].toString(16).padStart(2, '0');
		const g = pixelData[1].toString(16).padStart(2, '0');
		const b = pixelData[2].toString(16).padStart(2, '0');

		return `#${r}${g}${b}`.toUpperCase();

	}

	/**
	 * Gestion du début du toucher
	 */
	onTouchStart(event: TouchEvent): void {
		event.preventDefault();

		this.touchStartTime = Date.now();
		this.isLongPress = false;

		if (event.touches.length === 1) {
			// Un seul doigt : déplacement ou placement de pixel
			const touch = event.touches[0];
			this.isTouchDragging = true;
			this.dragStartX = touch.clientX;
			this.dragStartY = touch.clientY;
			this.cameraStartX = this.cameraX;
			this.cameraStartY = this.cameraY;

			// ✅ Détecter un appui long (500ms)
			this.longPressTimeout = setTimeout(() => {
				this.isLongPress = true;
				const worldPos = this.screenToWorld(touch.clientX, touch.clientY);
				const pixelX = Math.floor(worldPos.x);
				const pixelY = Math.floor(worldPos.y);

				if (pixelX >= 0 && pixelX < this.CANVAS_WIDTH &&
					pixelY >= 0 && pixelY < this.CANVAS_HEIGHT) {
					this.removePixel(pixelX, pixelY);

					// Vibration tactile si disponible
					if ('vibrate' in navigator) {
						navigator.vibrate(50);
					}
				}
			}, 500);
		} else if (event.touches.length === 2) {
			// Deux doigts : pinch-to-zoom
			clearTimeout(this.longPressTimeout);
			this.isTouchDragging = false;
			const touch1 = event.touches[0];
			const touch2 = event.touches[1];

			this.lastTouchDistance = this.getTouchDistance(touch1, touch2);
			this.lastTouchCenter = this.getTouchCenter(touch1, touch2);
		}
	}

	/**
	 * Gestion du mouvement tactile
	 */
	onTouchMove(event: TouchEvent): void {
		event.preventDefault();

		if (event.touches.length === 1 && this.isTouchDragging) {
			// Annuler le long press si l'utilisateur bouge son doigt
			const touch = event.touches[0];
			const deltaX = Math.abs(touch.clientX - this.dragStartX);
			const deltaY = Math.abs(touch.clientY - this.dragStartY);

			if (deltaX > 10 || deltaY > 10) {
				clearTimeout(this.longPressTimeout);
				this.isLongPress = false;
			}

			// Déplacement avec un doigt
			const moveDeltaX = touch.clientX - this.dragStartX;
			const moveDeltaY = touch.clientY - this.dragStartY;

			this.cameraX = this.cameraStartX - moveDeltaX / this.zoom;
			this.cameraY = this.cameraStartY - moveDeltaY / this.zoom;

			this.clampCamera();
			this.onCameraMove();
			this.requestRender();
		} else if (event.touches.length === 2) {
			// Pinch-to-zoom avec deux doigts
			const touch1 = event.touches[0];
			const touch2 = event.touches[1];

			const currentDistance = this.getTouchDistance(touch1, touch2);
			const currentCenter = this.getTouchCenter(touch1, touch2);

			if (this.lastTouchDistance && this.lastTouchCenter) {
				// Calculer le zoom
				const zoomFactor = currentDistance / this.lastTouchDistance;
				const newZoom = this.zoom * zoomFactor;

				// Calculer la position mondiale du centre du pinch
				const canvas = this.canvasRef.nativeElement;
				const rect = canvas.getBoundingClientRect();
				const centerX = currentCenter.x - rect.left;
				const centerY = currentCenter.y - rect.top;

				const worldX = this.cameraX + centerX / this.zoom;
				const worldY = this.cameraY + centerY / this.zoom;

				// Appliquer le zoom
				this.zoom = Math.max(0.19, Math.min(50, newZoom));

				// Ajuster la caméra pour zoomer vers le centre du pinch
				this.cameraX = worldX - centerX / this.zoom;
				this.cameraY = worldY - centerY / this.zoom;

				// Déplacement pendant le pinch
				const deltaCenterX = currentCenter.x - this.lastTouchCenter.x;
				const deltaCenterY = currentCenter.y - this.lastTouchCenter.y;

				this.cameraX -= deltaCenterX / this.zoom;
				this.cameraY -= deltaCenterY / this.zoom;

				this.clampCamera();
				this.onCameraMove();
				this.requestRender();
			}

			this.lastTouchDistance = currentDistance;
			this.lastTouchCenter = currentCenter;
		}
	}

	/**
	 * Gestion de la fin du toucher
	 */
	onTouchEnd(event: TouchEvent): void {
		event.preventDefault();

		// Annuler le long press
		clearTimeout(this.longPressTimeout);

		const touchDuration = Date.now() - this.touchStartTime;

		if (event.touches.length === 0) {
			// Tous les doigts sont levés

			// Ne placer de pixel que si c'est un tap rapide ET pas un long press
			if (this.isTouchDragging && touchDuration < 200 && !this.isLongPress) {
				// C'est un tap rapide (pas un drag ni un long press) : placer un pixel
				const touch = event.changedTouches[0];
				const worldPos = this.screenToWorld(touch.clientX, touch.clientY);
				const pixelX = Math.floor(worldPos.x);
				const pixelY = Math.floor(worldPos.y);

				if (pixelX >= 0 && pixelX < this.CANVAS_WIDTH &&
					pixelY >= 0 && pixelY < this.CANVAS_HEIGHT) {

					if (this.selectedColour && this.canPlacePixel) {
						this.placePixel(pixelX, pixelY, this.selectedColour);
						this.selectedPixel = null;
					}

					this.selectedPixel = {x: pixelX, y: pixelY};
					this.requestRender();
				}
			}

			this.isTouchDragging = false;
			this.isLongPress = false;
			this.lastTouchDistance = null;
			this.lastTouchCenter = null;
		} else if (event.touches.length === 1) {
			// Un doigt reste : réinitialiser pour le drag
			const touch = event.touches[0];
			this.isTouchDragging = true;
			this.dragStartX = touch.clientX;
			this.dragStartY = touch.clientY;
			this.cameraStartX = this.cameraX;
			this.cameraStartY = this.cameraY;
			this.lastTouchDistance = null;
			this.lastTouchCenter = null;
			this.isLongPress = false;
		}
	}

	/**
	 * Calcule la distance entre deux touches
	 */
	private getTouchDistance(touch1: Touch, touch2: Touch): number {
		const dx = touch2.clientX - touch1.clientX;
		const dy = touch2.clientY - touch1.clientY;
		return Math.sqrt(dx * dx + dy * dy);
	}

	/**
	 * Calcule le centre entre deux touches
	 */
	private getTouchCenter(touch1: Touch, touch2: Touch): { x: number; y: number } {
		return {
			x: (touch1.clientX + touch2.clientX) / 2,
			y: (touch1.clientY + touch2.clientY) / 2
		};
	}
}
